import { useMemo } from 'react';
import { LogOut, MapPin } from 'lucide-react';
import { BaseDialog } from '@/components/BaseDialog';
import { Button as CustomButton } from '@/components/Button/CustomButton';
import { CustomAutocomplete } from '@/components/CustomAutocomplete/CustomAutocomplete';
import { useAuth } from '@/contexts/AuthContext';
import { useSelectedOrganisation } from '@/contexts/SelectedOrganisationContext';
import { ALL_ORGANISATIONS } from '@/hooks/useOrganisationFilter';
import { usePermissions } from '@/hooks/usePermissions';
import { useSwitchableOrganisations } from '@/hooks/useOrganisations';

interface OrganisationOption {
	id: string;
	label: string;
}

interface UserMenuDialogProps {
	open: boolean;
	onClose: () => void;
}

export default function UserMenuDialog({ open, onClose }: UserMenuDialogProps) {
	const { userProfile, logout } = useAuth();
	const { canChooseOrganisation } = usePermissions();
	const { organisations, loading } = useSwitchableOrganisations(canChooseOrganisation);
	const { selectedOrganisationId, setSelectedOrganisationId } = useSelectedOrganisation();

	const options = useMemo<OrganisationOption[]>(
		() => [
			{ id: ALL_ORGANISATIONS, label: 'All organisations' },
			...organisations.map((organisation) => ({ id: String(organisation.id), label: organisation.name })),
		],
		[organisations],
	);

	if (!userProfile) return null;

	const handleLogout = () => {
		onClose();
		logout();
	};

	return (
		<BaseDialog
			open={open}
			onClose={onClose}
			title="Account"
			maxWidth="440px"
			actions={
				<CustomButton
					variant="outlined"
					sx={{
						color: 'var(--error-500)',
						borderColor: 'var(--error-500)',
						'&:hover': { backgroundColor: 'var(--error-100)', borderColor: 'var(--error-500)' },
						'& .MuiTouchRipple-root .MuiTouchRipple-child': { backgroundColor: 'var(--error-300) !important' },
					}}
					icon={<LogOut size={18} />}
					text="Log out"
					onClick={handleLogout}
				/>
			}
		>
			<div className="flex flex-col gap-4">
				<div className="flex flex-wrap items-center gap-x-2">
					{/* Plain spans: .dialog-content restyles every MuiTypography. */}
					<span style={{ fontSize: 16, fontWeight: 500, color: 'var(--neutral-dark-base)' }}>
						{userProfile.fullName}
					</span>
					<span
						aria-hidden
						className="h-1 w-1 rounded-full"
						style={{ backgroundColor: 'var(--neutral-gray-medium)' }}
					/>
					<span style={{ fontSize: 16, color: 'var(--neutral-gray-medium)' }}>{userProfile.email}</span>
				</div>

				{userProfile.sites?.length > 0 && (
					<div className="flex items-start gap-1">
						<MapPin
							size={16}
							className="mt-0.5 shrink-0"
							color="var(--neutral-gray-medium)"
						/>
						<span style={{ fontSize: 14, color: 'var(--neutral-gray-medium)' }}>
							{userProfile.sites.map((site) => site.siteName).join(', ')}
						</span>
					</div>
				)}

				{canChooseOrganisation && (
					<CustomAutocomplete
						multiple={false}
						disableClearable
						options={options}
						loading={loading}
						disabled={loading}
						getOptionLabel={(option) => (option as OrganisationOption).label}
						value={options.find((option) => option.id === selectedOrganisationId) ?? options[0]}
						onChange={(_, newValue) =>
							setSelectedOrganisationId(newValue ? (newValue as OrganisationOption).id : ALL_ORGANISATIONS)
						}
						label="Organisation"
						slotProps={{ listbox: { sx: { '& .MuiAutocomplete-option': { fontSize: 16 } } } }}
					/>
				)}
			</div>
		</BaseDialog>
	);
}
